import React, { createContext, useCallback, useEffect, useMemo, useState } from "react";
import axios from "axios";
import {
  hasAdminCapability,
  isValidAdminSession,
} from "./adminSessionPolicy";

const AuthContext = createContext();

const AuthProvider = ({ children }) => {
  const [authenticated, setAuthenticatedState] = useState(false);
  const [initializing, setInitializing] = useState(true);
  const [adminSession, setAdminSession] = useState(null);
  const [adminMail, setAdminMail] = useState("");
  const [forgetPassword, setforgetPassword] = useState(false);
  const [beingSend, setBeingSend] = useState(false);
  const [open, setOpen] = useState(false);
  const [accessToken, setAccessTokenState] = useState("");
  const [playerData, setPlayerData] = useState([]);
  const [allPlayerData, setAllPlayerData] = useState([]);
  const [playerPagination, setPlayerPagination] = useState({
    page: 1,
    limit: 10,
    totalPages: 1,
    totalUsers: 0,
  });
  const [groundPagination, setGroundPagination] = useState({
    currentPage: 1,
    limit: 10,
    totalPages: 1,
    totalGrounds: 0,
  });
  const [paymentTransactions, setPaymentTransactions] = useState([]);
  const [paymentPagination, setPaymentPagination] = useState({
    currentPage: 1,
    totalPages: 1,
  });
  const [receiptData, setReceiptData] = useState([]);
  const [receiptPagination, setReceiptPagination] = useState({
    currentPage: 1,
    totalPages: 1,
    totalCounts: 0,
  });
  const [groundData, setGroundData] = useState([]);
  const [matchData, setMatchData] = useState([]);
  const [bookingData, setBookingData] = useState([]);
  const [teamData, setTeamData] = useState([]);
  const [admins, setAdmins] = useState([]);
  const [academies, setAcademies] = useState([]);
  const [academyRegistrations, setAcademyRegistrations] = useState([]);
  const [academyDetails, setAcademyDetails] = useState([]);
  const [promotions, setPromotions] = useState([]);
  const [selectedPromotionID, setSelectedPromotionID] = useState(null);
  const [sports, setSports] = useState([]);
  const [editPromotion, setEditPromotion] = useState(false);

  const clearSession = useCallback(() => {
    localStorage.removeItem("access_token");
    localStorage.removeItem("admin_role");
    setAccessTokenState("");
    setAdminSession(null);
    setAdminMail("");
    setAuthenticatedState(false);
  }, []);

  const refreshAdminSession = useCallback(
    async (token = localStorage.getItem("access_token")) => {
      if (!token) {
        clearSession();
        setInitializing(false);
        return null;
      }

      setInitializing(true);
      try {
        const response = await axios.get("api/admin/session", {
          baseURL: process.env.REACT_APP_API_URL,
          headers: { Authorization: `Bearer ${token}` },
          timeout: 10000,
        });
        const session = response?.data?.session;
        if (response?.data?.status !== true || !isValidAdminSession(session)) {
          throw new Error("Invalid administrator session response");
        }

        localStorage.setItem("access_token", token);
        localStorage.removeItem("admin_role");
        setAccessTokenState(token);
        setAdminSession(session);
        setAdminMail(session.email || "");
        setAuthenticatedState(true);
        return session;
      } catch (error) {
        clearSession();
        throw error;
      } finally {
        setInitializing(false);
      }
    },
    [clearSession]
  );

  useEffect(() => {
    refreshAdminSession().catch(() => undefined);
  }, [refreshAdminSession]);

  const requestAuthentication = useCallback(
    (nextAuthenticated) => {
      if (!nextAuthenticated) {
        clearSession();
        return Promise.resolve(null);
      }
      return refreshAdminSession().catch(() => null);
    },
    [clearSession, refreshAdminSession]
  );

  const setAccessToken = useCallback((token) => {
    if (token) {
      localStorage.setItem("access_token", token);
      setAccessTokenState(token);
      return;
    }
    localStorage.removeItem("access_token");
    setAccessTokenState("");
  }, []);

  const capabilities = useMemo(
    () => adminSession?.capabilities || [],
    [adminSession]
  );
  const hasCapability = useCallback(
    (capability) => hasAdminCapability(adminSession, capability),
    [adminSession]
  );
  const adminRole = adminSession?.role || null;

  return (
    <AuthContext.Provider
      value={{
        authenticated,
        initializing,
        adminSession,
        capabilities,
        hasCapability,
        refreshAdminSession,
        clearSession,
        setAuthenticated: requestAuthentication,
        adminRole,
        setAdminRole: () => undefined,
        adminMail,
        setAdminMail,
        setforgetPassword,
        forgetPassword,
        setBeingSend,
        beingSend,
        setAccessToken,
        accessToken,
        playerData,
        allPlayerData,
        playerPagination,
        setPlayerData,
        setAllPlayerData,
        setPlayerPagination,
        groundPagination,
        setGroundPagination,
        paymentTransactions,
        setPaymentTransactions,
        paymentPagination,
        setPaymentPagination,
        receiptData,
        setReceiptData,
        receiptPagination,
        setReceiptPagination,
        groundData,
        setGroundData,
        matchData,
        setMatchData,
        bookingData,
        setBookingData,
        teamData,
        setTeamData,
        open,
        setOpen,
        admins,
        setAdmins,
        academies,
        setAcademies,
        academyRegistrations,
        setAcademyRegistrations,
        academyDetails,
        setAcademyDetails,
        promotions,
        setPromotions,
        selectedPromotionID,
        setSelectedPromotionID,
        editPromotion,
        setEditPromotion,
        sports,
        setSports,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};

export { AuthContext, AuthProvider };
